import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { Box } from "@mui/material";
import { useUserPromoCodes } from "api/usePromoCodesQuery";
import { ExpandableSection, Spacing, Spinner } from "@mindlercare/ui-web";
import type { PromoCode } from "core/types";
import { useTranslation } from "react-i18next";
import { Typography } from "components/typography";
import { PromoCodesTable } from "./PromoCodesTable";

const splitIntoActiveAndInactive = (promocodes: PromoCode[]) =>
  promocodes.reduce<PromoCode[][]>(
    ([active, inactive], promoCode) => {
      const now = Date.now() / 1000; // up to seconds
      if (promoCode.deletedAt || now > promoCode.expiration)
        return [active, [...inactive, promoCode]];

      return [[...active, promoCode], inactive];
    },
    [[], []],
  );

export const PromoCodes = ({
  patient,
}: {
  patient?: RouterOutput["patients"]["getByUserId"];
}) => {
  const { t } = useTranslation();
  const { data, isLoading } = useUserPromoCodes(patient?.userId);

  const getCodesTranslation = (value: number) =>
    value === 1 ? t("PROMOCODE.one") : t("PROMOCODE.many");

  const [active, inactive] = splitIntoActiveAndInactive(data ?? []);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Box sx={{ mb: "1rem" }} />
      <ExpandableSection expanded={active.length > 0}>
        <ExpandableSection.Toggle disabled={active.length === 0}>
          <Typography insensitive variant="h4">
            {t("ACTIVE_PROMOCODES")}
          </Typography>
          <Spacing l={4} />
          <Typography variant="body" color="GRAY_DARK">
            {`- ${active.length || 0} ${getCodesTranslation(active.length)}`}
          </Typography>
        </ExpandableSection.Toggle>
        <ExpandableSection.Content>
          <PromoCodesTable source={active} />
        </ExpandableSection.Content>
      </ExpandableSection>
      <ExpandableSection>
        <ExpandableSection.Toggle disabled={inactive.length === 0}>
          <Typography insensitive variant="h4">
            {t("INACTIVE_PROMOCODES")}
          </Typography>
          <Spacing l={4} />
          <Typography variant="body" color="GRAY_DARK">
            {`- ${inactive.length || 0} ${getCodesTranslation(
              inactive.length,
            )}`}
          </Typography>
        </ExpandableSection.Toggle>
        <ExpandableSection.Content>
          <PromoCodesTable source={inactive} />
        </ExpandableSection.Content>
      </ExpandableSection>
    </>
  );
};
